import { Dialog, IconButton } from '@material-ui/core';
import PropTypes from 'prop-types';
import React from 'react';
import { FaArrowLeft, FaArrowRight, FaSpinner } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import { Box, ButtonFilled, ButtonOutlined, SvgIcon, Text, Image, Flex } from 'Components/Base';
import { useOffset } from 'Context/LayoutContext/OffsetProvider';

/** ---------------------------
 * Styled
 * -------------------------- */

const BaseBtnCss = css`
  width: 100%;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
`;

const StyledButtonFilled = styled(ButtonFilled)`
  ${BaseBtnCss};
`;

const StyledButtonOutlined = styled(ButtonOutlined)`
  ${BaseBtnCss};
`;

const NextBtn = styled(StyledButtonFilled)`
  gap: 8px;
`;

const BackBtn = styled(StyledButtonOutlined)`
  max-width: 100px;
  gap: 8px;
`;

const LastBtn = styled(StyledButtonFilled)``;

const ThirdBtn = styled(StyledButtonFilled)`
  position: relative;
`;

const TrialBadge = styled(Image)`
  position: absolute;
  right: 0;
  top: -39px;
`;

const Shell = styled(Flex)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
`;

const Content = styled(Flex)`
  width: 100%;
  max-width: 375px; /* keep mobile style centered on larger screens */
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled(Flex)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  width: 100%;
`;

const CloseBtnWrap = styled(Box)`
  position: absolute;
  right: 16px;
  top: ${({ topOffset }) => (topOffset ? topOffset + 32 : 32)}px;
  transform: translateY(-50%);
  z-index: 10;
`;

const ProgressWrap = styled(Flex)`
  display: flex;
  gap: 8px;
  padding-top: 8px;
`;

const ProgressPill = styled((props) => <Box {...props} />)`
  height: 6px;
  width: 48px;
  border-radius: 999px;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.bsGrays.gray200};
  transition: background 150ms ease;
`;

const BulletRow = styled(Flex)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BottomBar = styled(Flex)`
  margin-top: auto;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
  position: sticky;
  bottom: ${({ bottomOffset }) => (bottomOffset ? bottomOffset : 16)}px;
  padding: 16px;
`;

const BottomHelperText = styled(Text)`
  width: 100%;
  text-align: center;
`;

const BottomActionsInner = styled(Flex)`
  width: 100%;
  gap: 12px;
  align-items: center;
`;

const CenteredLoading = styled(Flex)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding-top: calc(114px - 40px);
`;

function Title({ subtitle }) {
  return (
    <>
      <Text textStyle="heading4" mb={1} textAlign="center">
        อัปเกรดการเรียน TCAS แบบ
        <Text as="span" color="primary">
          ใหม่!
        </Text>
      </Text>
      <Text textStyle="caption" color="textColor.secondary" mb={4} textAlign="center">
        {subtitle}
      </Text>
    </>
  );
}

function ImageWrapper({ image, alt }) {
  const [hasError, setHasError] = React.useState(false);

  if (hasError) {
    return (
      <Box
        mb={6}
        height="200px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        backgroundColor="bsGrays.gray100"
        borderRadius="8px"
      >
        <Text textStyle="caption" color="textColor.secondary">
          ไม่สามารถโหลดรูปภาพได้
        </Text>
      </Box>
    );
  }

  return <Image src={image} alt={alt} mb={6} onError={() => setHasError(true)} />;
}
ImageWrapper.propTypes = {
  image: PropTypes.string.isRequired,
  alt: PropTypes.string,
};

function BulletHeader({ text }) {
  return (
    <Text textStyle="heading5" fontWeight="bold" mb={3}>
      {text}
    </Text>
  );
}
BulletHeader.propTypes = { text: PropTypes.string.isRequired };

function ContentBullets({ bullets }) {
  return (
    <Box>
      {bullets.map((bullet) => (
        <BulletRow mb={2} key={bullet}>
          <SvgIcon icon={SvgIcon.ICON_LIST.CHECK_CIRCLE} size={20} color="primary" />
          <Text textStyle="body1">{bullet}</Text>
        </BulletRow>
      ))}
    </Box>
  );
}
ContentBullets.propTypes = { bullets: PropTypes.arrayOf(PropTypes.string).isRequired };

function DefaultScreen1({ subtitle }) {
  return (
    <>
      <Title subtitle={subtitle} />
      <ImageWrapper
        image="/assets/images/myCoursesBanner/eggSpace/TK-onboard-freetrial-1.svg"
        alt="onboard-freetrial-1"
      />
      <BulletHeader text="เรียนแบบตะลุย ‘ภารกิจ’" />
      <ContentBullets bullets={['ระบบฝึกโจทย์ไม่ซ้ำ', 'ระบบสะสมไข่ ให้ทุกความพยายามมีความหมาย']} />
    </>
  );
}

function DefaultScreen2({ subtitle }) {
  return (
    <>
      <Title subtitle={subtitle} />
      <ImageWrapper
        image="/assets/images/myCoursesBanner/eggSpace/TK-onboard-freetrial-2.svg"
        alt="onboard-freetrial-2"
      />
      <BulletHeader text="เฉลยละเอียด เข้าใจได้ทุกพื้นฐาน" />
      <ContentBullets bullets={['แบบฝึกหัดรูปแบบใหม่ เฉลยทันที', 'ผ่านการกำกับโดยทีมวิชาการ']} />
    </>
  );
}

function DefaultScreen3({ subtitle }) {
  return (
    <>
      <Title subtitle={subtitle} />
      <ImageWrapper
        image="/assets/images/myCoursesBanner/eggSpace/TK-onboard-freetrial-3.svg"
        alt="onboard-freetrial-3"
      />
      <BulletHeader text="ฝึกแก้มือจนมั่นใจ" />
      <ContentBullets
        bullets={['สรุปข้อที่พลาดหรือไม่มั่นใจให้ทบทวนอีกครั้ง', 'ให้โอกาสทำข้อที่พลาดใหม่']}
      />
    </>
  );
}

function DefaultScreen4() {
  return (
    <CenteredLoading>
      <Box width="100%" maxWidth="320px" mb={18}>
        <Image
          src="/assets/images/myCoursesBanner/eggSpace/TK-onboard-freetrial-4.svg"
          alt="onboard-freetrial-4"
        />
      </Box>

      <Flex alignItems="center" justifyContent="center" mb={2} gridColumnGap={2}>
        <SvgIcon icon={SvgIcon.ICON_LIST.CHECK_CIRCLE} size={24} color="success" />
        <Text textStyle="heading4" fontWeight={800}>
          กำลังเตรียมคอร์สให้นะ
        </Text>
      </Flex>

      <Text textStyle="body1" align="center" px={18} mb={4}>
        คอร์สจะแสดงผลที่หน้าหลัก ภายใน 30 นาที
      </Text>
      <Text textStyle="caption" color="textColor.secondary" px={18}>
        หากเกินกว่านั้นคอร์สยังไม่แสดงผล ให้ลองปิดแอปพลิเคชันแล้วเข้าใหม่อีกครั้ง
      </Text>
    </CenteredLoading>
  );
}

/** ---------------------------
 * Helpers
 * -------------------------- */

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function getDefaultScreens({ subtitle }) {
  return [
    { key: 's1', render: () => <DefaultScreen1 subtitle={subtitle} /> },
    { key: 's2', render: () => <DefaultScreen2 subtitle={subtitle} /> },
    { key: 's3', render: () => <DefaultScreen3 subtitle={subtitle} /> },
    { key: 's4', render: () => <DefaultScreen4 /> },
  ];
}

function TopNavigation({ show, step, total, onClose }) {
  if (!show) return null;

  const progressCount = Math.min(total, 3);

  return (
    <TopBar>
      <ProgressWrap>
        {Array.from({ length: progressCount }).map((_, idx) => (
          <ProgressPill key={idx} $active={idx === step} />
        ))}
      </ProgressWrap>
    </TopBar>
  );
}
TopNavigation.propTypes = {
  show: PropTypes.bool.isRequired,
  step: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

function BottomActions({
  isFirst,
  isLast,
  isThird,
  canBack,
  onBack,
  onNext,
  onClose,
  loading,
  bottomOffset,
}) {
  return (
    <BottomBar bottomOffset={bottomOffset}>
      {!isLast && (
        <BottomHelperText
          fontSize={14}
          fontWeight="semibold"
          color={isFirst ? 'textColor.primary' : 'myPath.error.500'}
        >
          {isFirst ? 'น้องอยู่ระดับชั้นไหน?' : 'เหมาะสำหรับนักเรียน ม.ปลาย'}
        </BottomHelperText>
      )}

      <BottomActionsInner>
        {isLast ? (
          <LastBtn onClick={onNext}>เข้าใจแล้ว</LastBtn>
        ) : isFirst ? (
          <>
            <StyledButtonFilled onClick={onClose} disabled={loading}>
              ระดับชั้นอื่น ๆ
            </StyledButtonFilled>

            <StyledButtonFilled onClick={onNext} disabled={loading}>
              อยู่ ม.ปลาย
            </StyledButtonFilled>
          </>
        ) : (
          <>
            {canBack ? (
              <BackBtn onClick={onBack} disabled={loading}>
                <FaArrowLeft /> ย้อน
              </BackBtn>
            ) : null}

            {isThird ? (
              <ThirdBtn onClick={onNext} disabled={loading}>
                <TrialBadge
                  src="/assets/images/myCoursesBanner/eggSpace/freetrial-button.svg"
                  alt="freetrial button"
                />
                {loading ? <FaSpinner /> : 'ลองเรียนเลย!'}
              </ThirdBtn>
            ) : (
              <NextBtn onClick={onNext}>
                ไปต่อ <FaArrowRight />
              </NextBtn>
            )}
          </>
        )}
      </BottomActionsInner>
    </BottomBar>
  );
}
BottomActions.propTypes = {
  isFirst: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
  isThird: PropTypes.bool.isRequired,
  canBack: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  bottomOffset: PropTypes.number,
};

/** ---------------------------
 * Component
 * -------------------------- */

export default function EggSpaceOnboarding({
  open,
  onClose,
  onEnroll,
  initialStep = 0,
  loading,
  subtitle,
}) {
  const mergedScreens = React.useMemo(() => {
    return getDefaultScreens({ subtitle });
  }, [subtitle]);
  const { topOffset, bottomOffset } = useOffset();

  const total = mergedScreens.length;

  const safeInitial = React.useMemo(() => clamp(initialStep, 0, total - 1), [initialStep, total]);

  const [step, setStep] = React.useState(safeInitial);

  const preloadImages = React.useMemo(() => {
    return [
      '/assets/images/myCoursesBanner/eggSpace/TK-onboard-freetrial-1.svg',
      '/assets/images/myCoursesBanner/eggSpace/TK-onboard-freetrial-2.svg',
      '/assets/images/myCoursesBanner/eggSpace/TK-onboard-freetrial-3.svg',
      '/assets/images/myCoursesBanner/eggSpace/freetrial-button.svg',
    ];
  }, []);

  React.useEffect(() => {
    if (open) setStep(safeInitial);
  }, [open, safeInitial]);

  const isFirst = step === 0;
  const isLast = step === total - 1;
  const isThird = step === 2;
  const canBack = step > 0;

  const handleClose = () => onClose?.();

  const handleNext = () => {
    if (isThird) {
      onEnroll?.();
    }
    if (isLast) {
      onClose?.();
      return;
    }
    setStep((s) => Math.min(s + 1, total - 1));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const shouldShowTopBar = step < 3;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        style: {
          background: isLast ? '#EBF5FF' : '#fff',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: topOffset,
        },
      }}
    >
      <Shell>
        {preloadImages.map((image) => (
          <link key={image} rel="preload" as="image" href={image} />
        ))}

        {shouldShowTopBar && (
          <CloseBtnWrap topOffset={topOffset}>
            <IconButton onClick={onClose} aria-label="close">
              <SvgIcon icon={SvgIcon.ICON_LIST.CLOSE} size={24} />
            </IconButton>
          </CloseBtnWrap>
        )}

        <Content>
          <TopNavigation show={shouldShowTopBar} step={step} total={total} onClose={handleClose} />

          <Box mt={40} flex="1 1 auto" overflow="auto" pb={10} px={4}>
            {mergedScreens[step]?.render?.()}
          </Box>

          <BottomActions
            isFirst={isFirst}
            isLast={isLast}
            isThird={isThird}
            canBack={canBack}
            onBack={handleBack}
            onNext={handleNext}
            onClose={handleClose}
            loading={loading}
            bottomOffset={bottomOffset}
          />
        </Content>
      </Shell>
    </Dialog>
  );
}

EggSpaceOnboarding.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onEnroll: PropTypes.func,
  initialStep: PropTypes.number,
  loading: PropTypes.bool,
  subtitle: PropTypes.string,
};
